import httpClient from '../../api';
import { candidateLogType } from '../../enums';
import { safeGet } from '../../functions';

export default () => ({
  namespaced: true,
  state: () => ({
    data: null,
  }),
  getters: {
    candidate(state) {
      return safeGet(state.data, ['candidate']);
    },
    vacancy(state) {
      return safeGet(state.data, ['vacancy']);
    },
  },
  mutations: {
    setData(state, payload) {
      state.data = { ...payload };
    },
    setCandidate(state, payload) {
      if (state.data) {
        state.data.candidate = { ...payload };
      }
    },
    updateCandidate(state, payload) {
      if (state.data) {
        state.data.candidate = { ...state.data.candidate, ...payload };
      }
    },
    setResume(state, payload) {
      if (state.data) {
        state.data.candidate.resume = { ...payload };
      }
    },
    unsetResume(state) {
      if (state.data) {
        state.data.candidate.resume = null;
      }
    },
    setInterview(state, payload) {
      if (state.data) {
        state.data.candidate.interview = { ...payload };
      }
    },
    logout(state) {
      const vacancyId = localStorage.getItem('vacancyId');
      localStorage.removeItem(`vacancy_${vacancyId}_current_candidate`);
      state.data = null;
    },
  },
  actions: {
    async loginByHashId(context, { hashId, vacancyId }) {
      const { data: { data: { token } } } = await httpClient.post('candidates/login', { hash: hashId });
      if (vacancyId) {
        localStorage.setItem(`vacancy_${vacancyId}_current_candidate`, token);
      }
      httpClient.defaults.headers.common['X-Candidate-Authorization'] = `Bearer ${token}`;

      return token;
    },
    async changeToken(context, { token, vacancyId }) {
      if (vacancyId) {
        localStorage.setItem(`vacancy_${vacancyId}_current_candidate`, token);
      }
      httpClient.defaults.headers.common['X-Candidate-Authorization'] = `Bearer ${token}`;
    },
    async fetchData(context, { vacancyId = null, token = null } = {}, payload) {
      if (vacancyId && !token && !localStorage.getItem(`vacancy_${vacancyId}_current_candidate`)) {
        return;
      }

      if (vacancyId && token && !localStorage.getItem(`vacancy_${vacancyId}_current_candidate`)) {
        localStorage.setItem(`vacancy_${vacancyId}_current_candidate`, token);
      }

      let candidate;
      let fetchFieldsData;

      if (!safeGet(payload, ['fetchFields'])) {
        fetchFieldsData = ['candidate', 'vacancy'];
      } else {
        fetchFieldsData = safeGet(payload, ['fetchFields']);
      }

      if (process.browser) {
        const tokenStorage = localStorage.getItem(`vacancy_${vacancyId}_current_candidate`);
        if (tokenStorage) {
          httpClient.defaults.headers.common['X-Candidate-Authorization'] = `Bearer ${tokenStorage}`;
        }
      }

      try {
        candidate = await httpClient.get('candidates');
      } catch (e) {
        if (e.name === 'ApiError' && e.code === 401) {
          localStorage.removeItem(`vacancy_${vacancyId}_current_candidate`);
          return;
        }

        throw e;
      }

      const vacancy = await httpClient.get(`vacancies/${candidate.data.data.vacancyId}`);

      context.commit('setData', {
        candidate: fetchFieldsData.includes('candidate') ? candidate.data.data : null,
        vacancy: fetchFieldsData.includes('vacancy') ? vacancy.data.data : null,
      });

      if (fetchFieldsData.includes('vacancy')) {
        context.commit('vacancy/setVacancy', {
          isFetched: true,
          isFetching: false,
          fetchError: null,
          data: vacancy.data.data,
        }, { root: true });
      }
    },
    async fetchDataWithoutVacancy(context, { vacancyId = null } = {}, payload) {
      if (!vacancyId && !localStorage.getItem(`vacancy_${vacancyId}_current_candidate`)) {
        return;
      }

      let candidate;
      let fetchFieldsData;

      if (!safeGet(payload, ['fetchFields'])) {
        fetchFieldsData = ['candidate', 'vacancy'];
      } else {
        fetchFieldsData = safeGet(payload, ['fetchFields']);
      }

      try {
        candidate = await httpClient.get('candidates');
      } catch (e) {
        if (e.name === 'ApiError' && e.code === 401) {
          localStorage.removeItem(`vacancy_${vacancyId}_current_candidate`);
          return;
        }

        throw e;
      }

      context.commit('setData', {
        candidate: fetchFieldsData.includes('candidate') ? candidate.data.data : null,
      });
    },
    async fetchCandidateById(context, id) {
      const { data: { data } } = await httpClient.get(`candidates/${id}`);
      return data;
    },
    async fetchPublicTokenInfo(context, token) {
      const { data: { data } } = await httpClient.get(`candidates/public-token/${token}`);
      return data;
    },
    async fetchCandidateByToken(context, token) {
      const { data: { data } } = await httpClient.get(`candidates/candidate/full/${token}`);
      return data;
    },
    async refuse(context, { candidateId, message }) {
      await httpClient.post(`candidates/${candidateId}/refused`, { message });
    },
    async support(context, { candidateId, message, fields }) {
      await httpClient.post(`candidates/${candidateId}/support`, { message, fields });
    },
    async feedback(context, { candidateId, message }) {
      await httpClient.post(`candidates/${candidateId}/feedback`, { message });
    },
    async update(context, { id, params }) {
      const { data: { data } } = await httpClient.patch(`candidates/${id}`, params);
      context.commit('setCandidate', data);
    },
    async create(context, { vacancyId, params }) {
      const { data: { data } } = await httpClient.post(`vacancies/${vacancyId}/candidate`, params);
      return data;
    },
    async createWithoutInvitation(context, { vacancyId, params }) {
      const { data: { data } } = await httpClient.post(`vacancies/${vacancyId}/candidate/without_invitation`, params);
      return data;
    },
    async createTest(context, { vacancyId, params }) {
      const { data: { data } } = await httpClient.post(`vacancies/${vacancyId}/candidate_test`, params);
      return data;
    },
    async updateTestEmail(context, { candidateId, params }) {
      await httpClient.post(`candidates/${candidateId}/update/test_email`, params);
    },
    async sendCoverLetter(context, { candidateId, coverLetter }) {
      await httpClient.post(`candidates/${candidateId}/log`, {
        eventType: candidateLogType.COVER_LETTER,
        comment: coverLetter,
      });
    },
    async storeResume(context, { candidateId, resume }) {
      const form = new FormData();
      form.append('resume', resume);
      const { data: { data } } = await httpClient.post(`candidates/${candidateId}/resume`, form);
      context.commit('setResume', data);
    },
    async destroyResume(context, resumeId) {
      await httpClient.delete(`candidates/resume/${resumeId}`);
      context.commit('unsetResume');
    },
    async markCandidateSetupStarted(context, { candidateId }) {
      await httpClient.post(`candidates/${candidateId}/mark_candidate_setup_is_started`);
    },
    async startTest() {
      await httpClient.post('candidates/test/start');
    },
    async answerQuestion(context, { testQuestionId, answer }) {
      await httpClient.post(`candidates/test/answer_question/${testQuestionId}`, answer);
    },
    async finishATest() {
      await httpClient.post('candidates/test/finish');
    },
    async getCandidateTestAnswers() {
      const { data: { data } } = await httpClient.get('candidates/test/answers');
      return data;
    },
    async fetchCandidateTest(context, { candidateId }) {
      const { data: { data } } = await httpClient.get(`candidates/${candidateId}/test`);
      return data;
    },
  },
});
